import { useCallback, useEffect, useState, type FC, type PropsWithChildren } from 'react';
import { axios, isInvalidGrant, isTokenExpired } from './axios';
import { AuthProgressLoader } from '@/components/auth/auth-progress-loader';
import { logError, logInfo } from '@/utils/errorReporter';
import { removeFromLocalStorage } from '@/local-storage/utils';
import { authRequest } from '@/auth/authConfig';
import { useLocation, useNavigate } from '@tanstack/react-router';
import i18n from '@/i18n/i18n';
import { getAzureB2CLocale } from '@/utils/locales';
import { useMsalAccount } from '@/auth/use-msal-account';

export const AxiosProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isRequestInterceptorReady, setIsRequestInterceptorReady] = useState(false);
  const [isResponseInterceptorReady, setIsResponseInterceptorReady] = useState(false);
  const { msalInstance, activeAccount } = useMsalAccount();

  const navigate = useNavigate();
  const location = useLocation();

  const clearAuthenticationState = useCallback(async () => {
    logInfo('Clearing authentication state due to B2C errors', 'auth-state-cleared', {
      reason: 'b2c-errors',
    });

    // Clear MSAL cache
    await msalInstance.clearCache();

    // Clear local storage (legacy keys)
    removeFromLocalStorage('userOriginalPolicy');

    // Redirect to login page
    if (location.pathname !== '/') {
      navigate({ to: '/', replace: true });
    }
  }, [location.pathname, navigate, msalInstance]);

  const getAccessToken = useCallback(async () => {
    // Always use the unified auth policy for token acquisition/refresh
    const request = authRequest;

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...request,
        account: activeAccount ?? undefined,
        prompt: 'none',
      });

      return response.accessToken;
    } catch (error) {
      // Enhanced error logging
      logError('b2c-authentication-error', error, 'axios-setup');

      // Check if this is a token expiration or invalid grant error
      if (isTokenExpired(error) || isInvalidGrant(error)) {
        logInfo(
          'B2C authentication error detected - clearing auth state',
          'b2c-auth-error-detected',
          { action: 'clearing-auth-state' }
        );
        await clearAuthenticationState();
        return null;
      }

      // Use unified auth flow for redirect
      await msalInstance.acquireTokenRedirect({
        ...request,
        account: activeAccount ?? undefined,
        prompt: 'login',
      });

      return null;
    }
  }, [activeAccount, clearAuthenticationState, msalInstance]);

  const setupRequestInterceptor = useCallback(() => {
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        const currentLanguage = i18n.language || 'nl';
        const uiLocales = getAzureB2CLocale(currentLanguage);

        if (!activeAccount) {
          await clearAuthenticationState();
          return config;
        }

        try {
          const token = await getAccessToken();

          if (!token) {
            return Promise.reject(new Error('Token not available'));
          }

          config.headers.Authorization = `Bearer ${token}`;

          return config;
        } catch (error) {
          // Check if this is a B2C authentication error
          if (isTokenExpired(error) || isInvalidGrant(error)) {
            await clearAuthenticationState();
            return Promise.reject(new Error('Authentication required - please sign in again'));
          }

          msalInstance.loginRedirect({
            ...authRequest,
            extraQueryParameters: { ui_locales: uiLocales },
          });
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }
      },
      (error) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
    );
    setIsRequestInterceptorReady(true);
    return requestInterceptor;
  }, [activeAccount, clearAuthenticationState, getAccessToken, msalInstance]);

  const setupResponseInterceptor = useCallback(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const responseData = error.response.data;

          // Enhanced error detection
          if (isTokenExpired(responseData) || isInvalidGrant(responseData)) {
            logError('b2c-authentication-error', responseData, 'axios-setup');
            await clearAuthenticationState();
            return Promise.reject(new Error('Authentication required - please sign in again'));
          }
        }

        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
    setIsResponseInterceptorReady(true);
    return responseInterceptor;
  }, [clearAuthenticationState, clearAuthenticationState]);

  useEffect(() => {
    const responseInterceptorId = setupResponseInterceptor();
    const requestInterceptorId = setupRequestInterceptor();

    return () => {
      axios.interceptors.response.eject(responseInterceptorId);
      axios.interceptors.request.eject(requestInterceptorId);
    };
  }, [setupRequestInterceptor, setupResponseInterceptor]);

  if (!isRequestInterceptorReady || !isResponseInterceptorReady) {
    return <AuthProgressLoader />;
  }

  return children;
};
